'use client'

import React from 'react'

import { Arrow } from '../../icons'
import { Image, ImageProps } from '../Image'
import {
  ArrowButton,
  Bullet,
  BulletsContainer,
  CarouselContainer,
  Container,
  Item,
} from './styles'

const SWIPE_THRESHOLD = 10 as const

export interface CarouselProps {
  index?: number

  /**
   * Carousel infinite state
   */
  infinite?: boolean

  /**
   * Array of images
   */
  images: ImageProps[]

  /**
   * Set height
   */
  height?: number | string
}

export const Carousel = ({
  index = 0,
  infinite = false,
  images,
  height,
}: CarouselProps) => {
  const widthSpan = 100
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = React.useState(index)
  const [
    { isTouching, touchStartPosition, touchEndPosition, scrollXOffset },
    setTouchState,
  ] = React.useState({
    isTouching: false,
    touchStartPosition: 0,
    touchEndPosition: 0,
    scrollXOffset: 0,
  })

  React.useEffect(() => {
    setCurrentIndex(index)
  }, [index])

  const itemCount = images.length

  const prioritizedImageIndexes =
    currentIndex === 0
      ? [itemCount - 1, 0, 1]
      : currentIndex === itemCount - 1
      ? [itemCount - 2, itemCount - 1, 0]
      : [currentIndex - 1, currentIndex, currentIndex + 1]

  const prev = React.useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : infinite ? itemCount - 1 : i))
  }, [infinite, itemCount])

  const next = React.useCallback(() => {
    setCurrentIndex((i) => (i < itemCount - 1 ? i + 1 : infinite ? 0 : i))
  }, [infinite, itemCount])

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    setTouchState({
      isTouching: true,
      scrollXOffset: 0,
      touchStartPosition: e.targetTouches?.[0]?.clientX ?? 0,
      touchEndPosition: e.targetTouches?.[0]?.clientX ?? 0,
    })
  }, [])

  const handleTouchMove = React.useCallback(
    (e: React.TouchEvent) => {
      if (!containerRef.current) return

      const frameWidth = containerRef.current.offsetWidth

      const scrollXOffset =
        ((touchEndPosition - touchStartPosition) / frameWidth) * 100

      setTouchState((s) => ({
        ...s,
        touchEndPosition: e.targetTouches?.[0]?.clientX ?? 0,
        scrollXOffset,
      }))
    },
    [touchEndPosition, touchStartPosition],
  )

  const handleTouchEnd = React.useCallback(() => {
    if (isTouching) {
      const distance = touchStartPosition - touchEndPosition
      if (distance > SWIPE_THRESHOLD) {
        next()
      } else if (distance < -SWIPE_THRESHOLD) {
        prev()
      }
    }

    setTouchState((s) => ({ ...s, isTouching: false, scrollXOffset: 0 }))
  }, [isTouching, next, prev, touchEndPosition, touchStartPosition])

  return (
    <Container>
      <CarouselContainer
        ref={containerRef}
        itemCount={itemCount}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((image, i) => (
          <Item
            key={i}
            itemCount={itemCount}
            translateX={-widthSpan * currentIndex + scrollXOffset}
            height={height}
          >
            <Image {...image} priority={prioritizedImageIndexes.includes(i)} />
          </Item>
        ))}
      </CarouselContainer>

      {itemCount > 1 && (
        <>
          {(currentIndex !== 0 || infinite) && (
            <ArrowButton onClick={prev} left>
              <Arrow direction="left" />
            </ArrowButton>
          )}

          {(currentIndex !== itemCount - 1 || infinite) && (
            <ArrowButton onClick={next}>
              <Arrow />
            </ArrowButton>
          )}

          <BulletsContainer itemCount={itemCount}>
            {images.map((_, i) => (
              <Bullet
                key={i}
                active={currentIndex === i}
                onClick={() => goToSlide(i)}
              />
            ))}
          </BulletsContainer>
        </>
      )}
    </Container>
  )
}
